/**
 * Life Domains Dialog
 * 
 * A dialog for managing life domains and their labels.
 * Classification properties (priority, type, intentional living) live on labels.
 */

import { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { Loader } from '@/components/ai-elements/loader'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label as UILabel } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { GripVertical, ArrowLeft, Plus, LifeDomainsIcon, LifeDomainsEmptyIcon, CalendarIcon, TaskIcon, Repeat, Edit, Tag, ChevronDown } from '@/components/icons'
import { useLifeDomains } from '@/context/life-domains-context'
import type { Priority, EventsType, LifeDomain, Label } from '@/types'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

const PRESET_COLORS = [
  '#7986cb', '#33b679', '#8e24aa', '#e67c73', '#f6bf26',
  '#f4511e', '#039be5', '#616161', '#3f51b5', '#0b8043',
  '#d50000',
]

function ColorPicker({ color, onChange }: { color: string; onChange: (color: string) => void }) {
  const [open, setOpen] = useState(false)

  const handleSelect = (c: string) => {
    onChange(c)
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 h-8 px-2 rounded-md border border-input bg-background hover:bg-accent transition-colors flex-shrink-0"
          aria-label="Pick color"
        >
          <div
            className="w-5 h-5 rounded-full border border-border flex-shrink-0"
            style={{ backgroundColor: color }}
          />
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-3" align="start">
        <div className="flex flex-wrap gap-2 max-w-[200px]">
          {PRESET_COLORS.map((preset) => (
            <button
              key={preset}
              type="button"
              onClick={() => handleSelect(preset)}
              className={cn(
                "w-7 h-7 rounded-full border-2 transition-all hover:scale-110",
                color.toLowerCase() === preset.toLowerCase()
                  ? "border-foreground ring-2 ring-foreground/20"
                  : "border-transparent"
              )}
              style={{ backgroundColor: preset }}
              aria-label={`Select color ${preset}`}
            />
          ))}
          {/* Custom color button */}
          <label
            className={cn(
              "w-7 h-7 rounded-full border-2 border-dashed border-muted-foreground/50 flex items-center justify-center cursor-pointer hover:border-foreground transition-colors relative",
              !PRESET_COLORS.includes(color.toLowerCase()) && "border-foreground ring-2 ring-foreground/20"
            )}
            aria-label="Pick custom color"
          >
            <Plus className="h-3.5 w-3.5 text-muted-foreground" />
            <input
              type="color"
              value={color}
              onChange={(e) => onChange(e.target.value)}
              className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
            />
          </label>
        </div>
      </PopoverContent>
    </Popover>
  )
}

// Sortable Life Domain Item Component
interface SortableLifeDomainProps {
  domain: LifeDomain
  labelCount: number
  defaultLabel?: Label
  onEdit: (id: string) => void
}

function SortableLifeDomain({ domain, labelCount, defaultLabel, onEdit }: SortableLifeDomainProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: domain.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="p-4 border-b border-border flex items-center gap-4 bg-card relative"
      {...attributes}
    >
      {/* Drag Handle */}
      <div
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground transition-colors"
      >
        <GripVertical className="h-5 w-5" />
      </div>

      {/* Domain color dot */}
      <div
        className="w-4 h-4 rounded-full flex-shrink-0 border border-border"
        style={{ backgroundColor: domain.color || '#3b82f6' }}
      />

      <div className="flex-1 min-w-0">
        <h3 className="font-semibold">{domain.title}</h3>
        
        {/* Show badges for default label properties */}
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          {/* Label count (excluding default) -- only shown when custom labels exist */}
          {labelCount > 1 && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-secondary text-secondary-foreground text-xs h-6">
              <Tag className="h-3 w-3" />
              {labelCount - 1}
            </span>
          )}
          <span className="text-xs text-muted-foreground">Default</span>
          {defaultLabel && (
            <span className={cn(
              "inline-flex items-center px-2 py-1 rounded-md text-xs font-medium h-6",
              defaultLabel.priority === 'High' && "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300",
              defaultLabel.priority === 'Medium' && "bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300",
              defaultLabel.priority === 'Low' && "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300",
              (!defaultLabel.priority || defaultLabel.priority === 'No priority') && "bg-muted text-muted-foreground"
            )}>
              {defaultLabel.priority && defaultLabel.priority !== 'No priority' ? defaultLabel.priority : 'No priority'}
            </span>
          )}
          {defaultLabel?.intentional_living && (
            <span className="inline-flex items-center justify-center px-2 py-1 rounded-md bg-primary/10 text-primary text-xs h-6">
              <LifeDomainsIcon className="h-4 w-4" />
            </span>
          )}
          {defaultLabel?.events_type === 'Time-block' && (
            <span className="inline-flex items-center justify-center px-2 py-1 rounded-md bg-secondary text-secondary-foreground text-xs h-6">
              <CalendarIcon className="h-4 w-4" />
            </span>
          )}
          {defaultLabel?.events_type === 'Tasks-block' && (
            <span className="inline-flex items-center justify-center px-2 py-1 rounded-md bg-secondary text-secondary-foreground text-xs h-6">
              <TaskIcon className="h-4 w-4" />
            </span>
          )}
          {defaultLabel?.events_type === 'Habit' && (
            <span className="inline-flex items-center justify-center px-2 py-1 rounded-md bg-secondary text-secondary-foreground text-xs h-6">
              <Repeat className="h-4 w-4" />
            </span>
          )}
        </div>
      </div>
      
      {/* Edit icon */}
      <button 
        type="button"
        onClick={() => onEdit(domain.id)}
        className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors flex-shrink-0"
        aria-label="Edit life domain"
      >
        <Edit className="h-6 w-6" />
      </button>
    </div>
  )
}

// Label editing form (inline within domain edit)
interface LabelFormProps {
  label?: Label
  isEditing: boolean
  onSave: (data: { name: string; description: string | null; priority: Priority | null; events_type: EventsType | null; intentional_living: boolean; color: string }) => void
  onCancel: () => void
  onDelete?: () => void
}

function LabelForm({ label, isEditing, onSave, onCancel, onDelete }: LabelFormProps) {
  const [name, setName] = useState(label?.name || '')
  const [description, setDescription] = useState(label?.description || '')
  const [priority, setPriority] = useState<Priority | null>(label?.priority || 'No priority')
  const [eventsType, setEventsType] = useState<EventsType | null>(label?.events_type || null)
  const [intentionalLiving, setIntentionalLiving] = useState(label?.intentional_living || false)
  const [color, setColor] = useState(label?.color || '#3b82f6')

  return (
    <Card className="border-dashed">
      <CardContent className="space-y-4 p-4">
        <div className="flex items-center gap-3">
          <ColorPicker color={color} onChange={setColor} />
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Label name"
            className="flex-1"
            disabled={label?.is_default}
          />
        </div>

        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Label description (optional)"
          className="resize-none"
          rows={2}
        />

        <div className="flex items-center justify-between">
          <UILabel className="min-w-fit">Priority</UILabel>
          <Select
            value={priority || 'No priority'}
            onValueChange={(value) => setPriority(value as Priority)}
          >
            <SelectTrigger className="w-40 shadow-none">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="High">High</SelectItem>
              <SelectItem value="Medium">Medium</SelectItem>
              <SelectItem value="Low">Low</SelectItem>
              <SelectItem value="No priority">No priority</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center justify-between">
          <UILabel className="min-w-fit">Events Type</UILabel>
          <Select
            value={eventsType || 'none'}
            onValueChange={(value) => setEventsType(value === 'none' ? null : value as EventsType)}
          >
            <SelectTrigger className="w-40 shadow-none">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              <SelectItem value="Tasks-block">Tasks-block</SelectItem>
              <SelectItem value="Time-block">Time-block</SelectItem>
              <SelectItem value="Habit">Habit</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center justify-between">
          <UILabel className="cursor-pointer">Intentional living</UILabel>
          <Switch
            checked={intentionalLiving}
            onCheckedChange={setIntentionalLiving}
            className="scale-125"
          />
        </div>

        <div className="flex gap-2 pt-2">
          {isEditing && onDelete && !label?.is_default && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onDelete}
              className="border-destructive text-destructive hover:bg-destructive/10 hover:text-destructive"
            >
              Delete
            </Button>
          )}
          <div className="flex-1" />
          {!label?.is_default && (
            <Button type="button" variant="outline" size="sm" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            onClick={() => onSave({ name, description: description.trim() || null, priority, events_type: eventsType, intentional_living: intentionalLiving, color })}
            disabled={!name.trim() && !label?.is_default}
          >
            Save
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

interface LifeDomainsDialogProps {
  open: boolean
  onClose: () => void
}

export function LifeDomainsDialog({ open, onClose }: LifeDomainsDialogProps) {
  const { 
    lifeDomains, 
    loading, 
    createDomain, 
    updateDomain, 
    deleteDomain,
    createLabel,
    updateLabel,
    deleteLabel,
    getLabelsForDomain,
    getDefaultLabel,
    refetch,
  } = useLifeDomains()
  
  const [lifeDomainView, setLifeDomainView] = useState<'list' | 'edit'>('list')
  const [editingDomain, setEditingDomain] = useState<string | null>(null)
  const [localDomains, setLocalDomains] = useState<LifeDomain[]>(lifeDomains)
  
  // Delete confirmation dialog state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [domainToDelete, setDomainToDelete] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)
  
  // Domain form state (no classification props -- those are on labels)
  const [formData, setFormData] = useState<{
    title: string
    description: string
    color: string
  }>({
    title: '',
    description: '',
    color: '#3b82f6',
  })

  // Label editing state
  const [editingLabelId, setEditingLabelId] = useState<string | null>(null)
  const [addingLabel, setAddingLabel] = useState(false)
  const [defaultLabelAutoExpanded, setDefaultLabelAutoExpanded] = useState(false)
  
  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )
  
  useEffect(() => {
    setLocalDomains(lifeDomains)
  }, [lifeDomains])
  
  useEffect(() => {
    if (!open) {
      setLifeDomainView('list')
      setEditingDomain(null)
      setFormData({ title: '', description: '', color: '#3b82f6' })
      setDeleteConfirmOpen(false)
      setDomainToDelete(null)
      setDeleting(false)
      setEditingLabelId(null)
      setAddingLabel(false)
      setDefaultLabelAutoExpanded(false)
    }
  }, [open])

  // Auto-expand default label once when entering edit mode for a domain
  useEffect(() => {
    if (editingDomain && !defaultLabelAutoExpanded && editingLabelId === null && !addingLabel) {
      const defaultLabel = getDefaultLabel(editingDomain)
      if (defaultLabel) {
        setEditingLabelId(defaultLabel.id)
        setDefaultLabelAutoExpanded(true)
      }
    }
  }, [editingDomain, defaultLabelAutoExpanded, editingLabelId, addingLabel, getDefaultLabel])
  
  // Life Domains handlers
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event

    if (!over || active.id === over.id) {
      return
    }

    const oldIndex = localDomains.findIndex((d) => d.id === active.id)
    const newIndex = localDomains.findIndex((d) => d.id === over.id)

    const reorderedDomains = arrayMove(localDomains, oldIndex, newIndex)
    setLocalDomains(reorderedDomains)

    try {
      const updatePromises = reorderedDomains.map((domain, index) => 
        updateDomain(domain.id, { sort_order: index })
      )
      await Promise.all(updatePromises)
      await refetch()
    } catch (error) {
      console.error('Failed to update sort order:', error)
      setLocalDomains(lifeDomains)
    }
  }
  
  const handleAddDomain = () => {
    setEditingDomain(null)
    setFormData({ title: '', description: '', color: '#3b82f6' })
    setEditingLabelId(null)
    setAddingLabel(false)
    setDefaultLabelAutoExpanded(false)
    setLifeDomainView('edit')
  }
  
  const handleEditDomain = async (domainId: string) => {
    const domain = lifeDomains.find(d => d.id === domainId)
    if (domain) {
      setEditingDomain(domainId)
      setFormData({
        title: domain.title,
        description: domain.description || '',
        color: domain.color || '#3b82f6',
      })
      setDefaultLabelAutoExpanded(false)
      setEditingLabelId(null)
      setAddingLabel(false)
      setLifeDomainView('edit')
    }
  }
  
  const handleDeleteDomain = (domainId: string) => {
    setDomainToDelete(domainId)
    setDeleteConfirmOpen(true)
  }
  
  const confirmDeleteDomain = async () => {
    if (!domainToDelete) return
    
    try {
      setDeleting(true)
      await deleteDomain(domainToDelete)
      setDeleteConfirmOpen(false)
      setDomainToDelete(null)
      setEditingDomain(null)
      setLifeDomainView('list')
    } catch (error) {
      console.error('Failed to delete life domain:', error)
    } finally {
      setDeleting(false)
    }
  }
  
  const handleSubmitDomain = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      if (editingDomain) {
        await updateDomain(editingDomain, {
          title: formData.title,
          description: formData.description || null,
          color: formData.color,
        })
        setFormData({ title: '', description: '', color: '#3b82f6' })
        setEditingDomain(null)
        setLifeDomainView('list')
      } else {
        // Create and then switch to edit mode so labels section is visible
        const newDomain = await createDomain({
          title: formData.title,
          description: formData.description || null,
          color: formData.color,
          sort_order: localDomains.length,
        })
        setEditingDomain(newDomain.id)
        setFormData({
          title: newDomain.title,
          description: newDomain.description || '',
          color: newDomain.color || '#3b82f6',
        })
      }
    } catch (error) {
      console.error('Failed to save life domain:', error)
    }
  }
  
  const handleBackToList = () => {
    setLifeDomainView('list')
    setEditingDomain(null)
    setFormData({ title: '', description: '', color: '#3b82f6' })
    setEditingLabelId(null)
    setAddingLabel(false)
  }
  
  const handleCloseDialog = () => {
    setLifeDomainView('list')
    setEditingDomain(null)
    onClose()
  }

  // Label handlers
  const handleSaveLabel = async (
    labelId: string | null,
    data: { name: string; description: string | null; priority: Priority | null; events_type: EventsType | null; intentional_living: boolean; color: string }
  ) => {
    try {
      if (labelId) {
        await updateLabel(labelId, {
          name: data.name,
          description: data.description,
          priority: data.priority,
          events_type: data.events_type,
          intentional_living: data.intentional_living,
          color: data.color,
        })
      } else if (editingDomain) {
        await createLabel({
          life_domain_id: editingDomain,
          name: data.name,
          description: data.description,
          priority: data.priority,
          events_type: data.events_type,
          intentional_living: data.intentional_living,
          color: data.color,
        })
      }
      setEditingLabelId(null)
      setAddingLabel(false)
    } catch (error) {
      console.error('Failed to save label:', error)
    }
  }

  const handleDeleteLabel = async (labelId: string) => {
    try {
      await deleteLabel(labelId)
      setEditingLabelId(null)
    } catch (error) {
      console.error('Failed to delete label:', error)
    }
  }

  // Get labels for the currently editing domain
  const currentDomainLabels = editingDomain ? getLabelsForDomain(editingDomain) : []

  return (
    <>
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleCloseDialog()}>
      <DialogContent 
        className="
          max-w-none
          p-0
          border-0 shadow-none
          w-full h-[80vh] max-h-[80vh]
          md:w-[800px] md:h-[70vh] md:max-h-[70vh]
          flex flex-col
          fixed !top-auto inset-x-0 !bottom-0 !left-0 !translate-x-0 !translate-y-0
          md:!left-1/2 md:!top-1/2 md:!bottom-auto md:inset-x-auto md:!-translate-x-1/2 md:!-translate-y-1/2
          rounded-t-3xl md:rounded-lg
        "
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        hideClose
      >
        <DialogDescription className="sr-only">
          {lifeDomainView === 'list' 
            ? 'Manage your life domains to organize and categorize your activities.' 
            : (editingDomain ? 'Edit an existing life domain.' : 'Create a new life domain to organize your activities.')}
        </DialogDescription>
        <DialogHeader className="px-2 pt-6 pb-4 flex-shrink-0">
          <div className="flex items-center justify-between gap-4">
            <Button
              variant="secondary"
              onClick={lifeDomainView === 'list' ? handleCloseDialog : handleBackToList}
              className="flex items-center gap-2 md:px-4 px-2 md:rounded-md rounded-full"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden md:inline">Back</span>
            </Button>
            
            <DialogTitle className="flex-1 text-center">
              {lifeDomainView === 'list' 
                ? 'Life domains' 
                : (editingDomain ? 'Edit Life domain' : 'Add a Life domain')}
            </DialogTitle>
            
            <div className="w-[40px] md:w-[72px]"></div>
          </div>
        </DialogHeader>
        
        {/* List View */}
        {lifeDomainView === 'list' && (
          <div className="px-2 pb-6 overflow-y-auto flex-1 min-h-0">
            <p className="text-sm text-muted-foreground mb-4 px-6">
              Define and order your Life domains. Each domain has labels with priority, type and intentional living settings.
            </p>
            {loading ? (
              <Card className="overflow-hidden">
                <CardContent className="p-0">
                  <div className="text-center py-8 text-muted-foreground">
                    Loading life domains...
                  </div>
                </CardContent>
              </Card>
            ) : localDomains.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground flex flex-col items-center">
                <LifeDomainsEmptyIcon className="h-16 w-16 text-muted-foreground mb-4" />
                <div>No Life Domains defined yet.</div>
                <div className="text-sm">Create your first one to get started!</div>
              </div>
            ) : (
              <Card className="overflow-hidden">
                <CardContent className="p-0">
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={localDomains.map(d => d.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div>
                        {localDomains.map((domain) => (
                          <SortableLifeDomain
                            key={domain.id}
                            domain={domain}
                            labelCount={getLabelsForDomain(domain.id).length}
                            defaultLabel={getDefaultLabel(domain.id)}
                            onEdit={handleEditDomain}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                </CardContent>
              </Card>
            )}
          </div>
        )}
        
        {/* Edit View - Life Domain */}
        {lifeDomainView === 'edit' && (
          <>
            <div className="px-6 overflow-y-auto flex-1 min-h-0">
              {/* Domain properties card */}
              <Card className="my-4">
                <CardContent className="space-y-4 p-4">
                  {/* Title + Color */}
                  <div className="flex items-end gap-3">
                    <ColorPicker
                      color={formData.color}
                      onChange={(color) => setFormData({ ...formData, color })}
                    />
                    <div className="flex-1 space-y-2">
                      <UILabel htmlFor="title">Title</UILabel>
                      <Input
                        id="title"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        placeholder="e.g., Health & Fitness"
                        required
                      />
                    </div>
                  </div>
                  {/* Action buttons */}
                  <div className="flex gap-3 pt-2">
                    {editingDomain && (
                      <Button 
                        type="button"
                        variant="outline"
                        onClick={() => handleDeleteDomain(editingDomain)}
                        className="flex-1 border-destructive text-destructive hover:bg-destructive/10 hover:text-destructive"
                      >
                        Delete
                      </Button>
                    )}
                    <Button 
                      type="button"
                      onClick={(e) => {
                        e.preventDefault()
                        handleSubmitDomain(e)
                      }}
                      className="flex-1"
                    >
                      Save
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Labels section -- only show when editing an existing domain */}
              {editingDomain && (
                <div className="my-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-sm flex items-center gap-2">
                      <Tag className="h-4 w-4" />
                      Labels ({currentDomainLabels.length})
                    </h3>
                    {!addingLabel && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => { setAddingLabel(true); setEditingLabelId(null) }}
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Add Label
                      </Button>
                    )}
                  </div>

                  <div className="space-y-2">
                    {currentDomainLabels.map((label) => (
                      editingLabelId === label.id ? (
                        <LabelForm
                          key={label.id}
                          label={label}
                          isEditing={true}
                          onSave={(data) => handleSaveLabel(label.id, data)}
                          onCancel={() => setEditingLabelId(null)}
                          onDelete={() => handleDeleteLabel(label.id)}
                        />
                      ) : (
                        <Card
                          key={label.id}
                          className="cursor-pointer hover:bg-muted/50 transition-colors"
                          onClick={() => { setEditingLabelId(label.id); setAddingLabel(false) }}
                        >
                          <CardContent className="p-3 flex items-center gap-3">
                            <div
                              className="w-4 h-4 rounded-full flex-shrink-0 border border-border"
                              style={{ backgroundColor: label.color }}
                            />
                            <span className="font-medium text-sm flex-1">{label.name}</span>
                            <div className="flex items-center gap-1.5 flex-wrap">
                              {label.is_default && (
                                <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">Default</span>
                              )}
                              <span className={cn(
                                  "text-xs px-1.5 py-0.5 rounded",
                                  label.priority === 'High' && "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300",
                                  label.priority === 'Medium' && "bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300",
                                  label.priority === 'Low' && "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300",
                                  (!label.priority || label.priority === 'No priority') && "bg-muted text-muted-foreground"
                                )}>
                                  {label.priority && label.priority !== 'No priority' ? label.priority : 'No priority'}
                                </span>
                              {label.intentional_living && (
                                <LifeDomainsIcon className="h-3.5 w-3.5 text-primary" />
                              )}
                              {label.events_type && (
                                <span className="text-xs px-1.5 py-0.5 rounded bg-secondary text-secondary-foreground">
                                  {label.events_type}
                                </span>
                              )}
                            </div>
                            <Edit className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          </CardContent>
                        </Card>
                      )
                    ))}

                    {addingLabel && (
                      <LabelForm
                        isEditing={false}
                        onSave={(data) => handleSaveLabel(null, data)}
                        onCancel={() => setAddingLabel(false)}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
        
        {/* Footer - Only show in list view */}
        {lifeDomainView === 'list' && (
          <DialogFooter className="px-6 py-4 flex-shrink-0 flex-col items-center sm:flex-row sm:justify-center">
            <Button 
              onClick={handleAddDomain}
              className="w-[240px] flex items-center justify-center gap-2"
            >
              <Plus className="h-4 w-4" />
              <span>Create Life Domain</span>
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>

    {/* Delete Confirmation Dialog */}
    <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
      <DialogContent className="max-w-md" hideClose>
        <DialogHeader>
          <DialogTitle>Delete Life Domain</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete this life domain? This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-center gap-3 mt-4">
          <Button
            variant="outline"
            onClick={() => {
              setDeleteConfirmOpen(false)
              setDomainToDelete(null)
            }}
            className="w-32"
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={confirmDeleteDomain}
            disabled={deleting}
            className="w-32"
          >
            {deleting ? (
              <>
                <Loader size={16} className="mr-2" />
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  )
}
